// frontend/src/components/Test/TestQuestion.js

import React, { useState, useEffect } from 'react';

const TestQuestion = ({
  question,
  currentNumber,
  totalQuestions,
  onAnswer,
  modoRespuesta = 'opcion-simple'
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedPair, setSelectedPair] = useState([]);
  const [textoRespuesta, setTextoRespuesta] = useState('');
  const [textoRespuesta2, setTextoRespuesta2] = useState('');
  const [masSeleccionado, setMasSeleccionado] = useState(null);
  const [menosSeleccionado, setMenosSeleccionado] = useState(null);
  const [multiSeleccion, setMultiSeleccion] = useState([]);
  const [escalaValor, setEscalaValor] = useState(null);
  const [answering, setAnswering] = useState(false);

  // Limpiar la selección al cambiar de pregunta
  useEffect(() => {
    setSelectedOption(null);
    setSelectedPair([]);
    setTextoRespuesta('');
    setTextoRespuesta2('');
    setMasSeleccionado(null);
    setMenosSeleccionado(null);
    setMultiSeleccion([]);
    setEscalaValor(null);
  }, [question?.id]);

  // Validar que question existe
  if (!question) {
    return (
      <div className="test-error">
        <p>Error: No se pudo cargar la pregunta</p>
      </div>
    );
  }

  const toggleParOpcion = (valor) => {
    setSelectedPair((prev) => {
      if (prev.includes(valor)) {
        return prev.filter((v) => v !== valor);
      }
      if (prev.length >= 2) {
        // Reemplaza la primera selección para que siempre queden como máximo 2
        return [prev[1], valor];
      }
      return [...prev, valor];
    });
  };

  const toggleMultiSeleccion = (valor) => {
    setMultiSeleccion((prev) => prev.includes(valor) ? prev.filter((v) => v !== valor) : [...prev, valor]);
  };

  const handleSubmit = async () => {
    if (modoRespuesta === 'opcion-multiple') {
      if (multiSeleccion.length === 0) {
        alert('Selecciona al menos una opción');
        return;
      }
      setAnswering(true);
      await onAnswer(JSON.stringify([...multiSeleccion].sort()));
      setAnswering(false);
      return;
    }

    if (modoRespuesta === 'texto-abierto') {
      if (!textoRespuesta.trim()) {
        alert('Por favor, escribe tu respuesta');
        return;
      }
      setAnswering(true);
      await onAnswer(textoRespuesta.trim());
      setAnswering(false);
      return;
    }

    if (modoRespuesta === 'escala') {
      if (escalaValor === null) {
        alert('Por favor, selecciona un valor en la escala');
        return;
      }
      setAnswering(true);
      await onAnswer(String(escalaValor));
      setAnswering(false);
      return;
    }

    if (modoRespuesta === 'cleaver-bloque') {
      if (!masSeleccionado || !menosSeleccionado) {
        alert('Selecciona la palabra que MÁS te describe y la que MENOS te describe');
        return;
      }
      setAnswering(true);
      await onAnswer({ masId: masSeleccionado, menosId: menosSeleccionado });
      setAnswering(false);
      return;
    }

    if (modoRespuesta === 'opcion-doble') {
      if (selectedPair.length !== 2) {
        alert('Esta serie requiere que selecciones exactamente dos respuestas');
        return;
      }
      setAnswering(true);
      await onAnswer(selectedPair.join(','));
      setAnswering(false);
      return;
    }

    if (modoRespuesta === 'texto-simple') {
      if (!textoRespuesta.trim()) {
        alert('Por favor, escribe tu respuesta');
        return;
      }
      setAnswering(true);
      await onAnswer(textoRespuesta.trim());
      setAnswering(false);
      return;
    }

    if (modoRespuesta === 'texto-doble') {
      if (!textoRespuesta.trim() || !textoRespuesta2.trim()) {
        alert('Esta serie requiere que escribas los dos números que faltan');
        return;
      }
      setAnswering(true);
      await onAnswer(`${textoRespuesta.trim()},${textoRespuesta2.trim()}`);
      setAnswering(false);
      return;
    }

    if (!selectedOption && selectedOption !== 0) {
      alert('Por favor, selecciona una respuesta');
      return;
    }

    setAnswering(true);
    await onAnswer(selectedOption);
    setAnswering(false);
  };

  const renderQuestionType = () => {
    if (modoRespuesta === 'cleaver-bloque') {
      const palabras = question.palabras || [];
      return (
        <div className="cleaver-bloque-container">
          <div className="cleaver-bloque-header">
            <span>Más</span>
            <span>Menos</span>
          </div>
          {palabras.map((palabra) => (
            <div key={palabra.id} className="cleaver-palabra-row">
              <label className="cleaver-radio-label">
                <input
                  type="radio"
                  name="cleaver-mas"
                  checked={masSeleccionado === palabra.id}
                  onChange={() => {
                    setMasSeleccionado(palabra.id);
                    if (menosSeleccionado === palabra.id) setMenosSeleccionado(null);
                  }}
                  disabled={answering}
                />
              </label>
              <span className="cleaver-palabra-texto">{palabra.texto}</span>
              <label className="cleaver-radio-label">
                <input
                  type="radio"
                  name="cleaver-menos"
                  checked={menosSeleccionado === palabra.id}
                  onChange={() => {
                    setMenosSeleccionado(palabra.id);
                    if (masSeleccionado === palabra.id) setMasSeleccionado(null);
                  }}
                  disabled={answering}
                />
              </label>
            </div>
          ))}
        </div>
      );
    }

    if (modoRespuesta === 'texto-abierto') {
      return (
        <div className="options-container">
          <textarea
            className="respuesta-texto-abierto"
            value={textoRespuesta}
            onChange={(e) => setTextoRespuesta(e.target.value)}
            disabled={answering}
            placeholder="Escribe tu respuesta aquí..."
            rows={5}
            autoFocus
          />
        </div>
      );
    }

    if (modoRespuesta === 'escala') {
      const { min = 1, max = 5, etiqueta_min: etiquetaMin, etiqueta_max: etiquetaMax } = question.opciones || {};
      const valores = [];
      for (let v = min; v <= max; v++) valores.push(v);
      return (
        <div className="options-container">
          <div className="escala-container">
            <div className="escala-opciones">
              {valores.map((v) => (
                <label key={v} className={`escala-opcion ${escalaValor === v ? 'escala-opcion-activa' : ''}`}>
                  <input
                    type="radio"
                    name="escala"
                    checked={escalaValor === v}
                    onChange={() => setEscalaValor(v)}
                    disabled={answering}
                  />
                  <span>{v}</span>
                </label>
              ))}
            </div>
            {(etiquetaMin || etiquetaMax) && (
              <div className="escala-etiquetas">
                <span>{etiquetaMin}</span>
                <span>{etiquetaMax}</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (modoRespuesta === 'texto-simple' || modoRespuesta === 'texto-doble') {
      return (
        <div className="options-container">
          <p className="hint-doble-respuesta">
            {modoRespuesta === 'texto-doble'
              ? 'Escribe los dos números que faltan'
              : 'Escribe tu respuesta'}
          </p>
          <div className="respuesta-numerica-container">
            <input
              type="text"
              className="respuesta-numerica-input"
              value={textoRespuesta}
              onChange={(e) => setTextoRespuesta(e.target.value)}
              disabled={answering}
              placeholder={modoRespuesta === 'texto-doble' ? 'Primer número' : 'Respuesta'}
              autoFocus
            />
            {modoRespuesta === 'texto-doble' && (
              <input
                type="text"
                className="respuesta-numerica-input"
                value={textoRespuesta2}
                onChange={(e) => setTextoRespuesta2(e.target.value)}
                disabled={answering}
                placeholder="Segundo número"
              />
            )}
          </div>
        </div>
      );
    }

    const options = question.opciones || {};
    const optionsArray = Object.entries(options).map(([key, value]) => ({
      valor: key,
      texto: value
    }));

    const opcionesFinales = optionsArray.length > 0
      ? optionsArray
      : [
          { valor: 'A', texto: 'Opción A' },
          { valor: 'B', texto: 'Opción B' },
          { valor: 'C', texto: 'Opción C' },
        ];

    if (modoRespuesta === 'opcion-doble') {
      return (
        <div className="options-container">
          <p className="hint-doble-respuesta">Selecciona las dos respuestas correctas</p>
          {opcionesFinales.map((option, index) => (
            <label key={index} className="option-label">
              <input
                type="checkbox"
                name="question"
                value={option.valor}
                checked={selectedPair.includes(option.valor)}
                onChange={() => toggleParOpcion(option.valor)}
                disabled={answering}
              />
              <span>
                {optionsArray.length > 0 ? option.texto : <><strong>{option.valor}.</strong> {option.texto}</>}
              </span>
            </label>
          ))}
        </div>
      );
    }

    if (modoRespuesta === 'opcion-multiple') {
      return (
        <div className="options-container">
          <p className="hint-doble-respuesta">Selecciona todas las opciones que correspondan</p>
          {opcionesFinales.map((option, index) => (
            <label key={index} className="option-label">
              <input
                type="checkbox"
                name="question"
                value={option.valor}
                checked={multiSeleccion.includes(option.valor)}
                onChange={() => toggleMultiSeleccion(option.valor)}
                disabled={answering}
              />
              <span>{option.texto}</span>
            </label>
          ))}
        </div>
      );
    }

    return (
      <div className="options-container">
        {opcionesFinales.map((option, index) => (
          <label key={index} className="option-label">
            <input
              type="radio"
              name="question"
              value={option.valor}
              checked={selectedOption === option.valor}
              onChange={(e) => setSelectedOption(e.target.value)}
              disabled={answering}
            />
            <span>
              {optionsArray.length > 0 ? option.texto : <><strong>{option.valor}.</strong> {option.texto}</>}
            </span>
          </label>
        ))}
      </div>
    );
  };

  return (
    <div className="question-card">
      <div className="question-header">
        <span className="question-number">
          Pregunta {currentNumber} de {totalQuestions}
        </span>
      </div>

      <div className="question-text">
        <div dangerouslySetInnerHTML={{ __html: question.texto || 'Texto de la pregunta no disponible' }} />
      </div>

      {renderQuestionType()}

      <div className="navigation-buttons">
        <button
          onClick={handleSubmit}
          className="btn-next"
          disabled={answering}
        >
          {answering ? 'Guardando...' : currentNumber === totalQuestions ? 'Finalizar Prueba' : 'Siguiente'}
        </button>
      </div>
    </div>
  );
};

export default TestQuestion;
